/**
 * Measure a SegmentedButton tab row and split it into visible tabs and a
 * "More" overflow, based on the container's real width.
 *
 * DOM contract (rendered by the consumer):
 * - `containerRef`: the row; its width must come from its parent (e.g.
 *   `flex-1 min-w-0`), never from the visible tabs, or it would oscillate.
 * - `measureRef`: a hidden mirror of the track, marked
 *   `data-tab-overflow="track"`, holding a SegmentedButton with every tab
 *   (same size/appearance/labels, no GTM attributes) and a "More" trigger copy
 *   marked `data-tab-overflow="more"`. Wrap it in an `aria-hidden` + `inert`
 *   zero-size, `overflow-hidden`, `invisible` box.
 * - `trailingRef` (optional): a real element after "More" (e.g. create stage)
 *   whose width is reserved.
 */

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  fitMeasuredTabs,
  type TabOverflowMetrics,
  type VisibleTabsResult,
} from '@/utils/status-tabs-overflow';

export interface UseTabOverflowOptions {
  tabCount: number;
  activeIndex: number;
  /** Changes whenever tab ids, labels, counts, or language change. */
  measureKey: string;
}

export interface UseTabOverflowResult extends VisibleTabsResult {
  containerRef: React.RefObject<HTMLDivElement | null>;
  measureRef: React.RefObject<HTMLDivElement | null>;
  trailingRef: React.RefObject<HTMLDivElement | null>;
  /** False until the first measurement; all tabs are reported visible. */
  isMeasured: boolean;
}

const px = (value: string): number => Number.parseFloat(value) || 0;

function readMetrics(
  container: HTMLElement,
  measure: HTMLElement,
  trailing: HTMLElement | null
): TabOverflowMetrics | null {
  const track = measure.querySelector<HTMLElement>('[data-tab-overflow="track"]');
  const more = measure.querySelector<HTMLElement>('[data-tab-overflow="more"]');
  if (!track || !more) return null;

  const trackStyle = getComputedStyle(track);
  const tabs = track.querySelectorAll<HTMLElement>('[data-slot="segmented-button-option"]');
  return {
    containerWidth: container.getBoundingClientRect().width,
    tabWidths: Array.from(tabs, (tab) => tab.getBoundingClientRect().width),
    tabGap: px(trackStyle.columnGap),
    trackPaddingX:
      px(trackStyle.paddingLeft) +
      px(trackStyle.paddingRight) +
      px(trackStyle.borderLeftWidth) +
      px(trackStyle.borderRightWidth),
    moreWidth: more.getBoundingClientRect().width,
    rowGap: px(getComputedStyle(container).columnGap),
    trailingWidth: trailing?.getBoundingClientRect().width ?? 0,
  };
}

const isSameMetrics = (a: TabOverflowMetrics | null, b: TabOverflowMetrics): boolean =>
  a != null &&
  a.containerWidth === b.containerWidth &&
  a.tabGap === b.tabGap &&
  a.trackPaddingX === b.trackPaddingX &&
  a.moreWidth === b.moreWidth &&
  a.rowGap === b.rowGap &&
  a.trailingWidth === b.trailingWidth &&
  a.tabWidths.length === b.tabWidths.length &&
  a.tabWidths.every((width, index) => width === b.tabWidths[index]);

export function useTabOverflow({
  tabCount,
  activeIndex,
  measureKey,
}: UseTabOverflowOptions): UseTabOverflowResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const trailingRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState<TabOverflowMetrics | null>(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const mirror = measureRef.current;
    if (!container || !mirror) return;
    const next = readMetrics(container, mirror, trailingRef.current);
    if (!next) return;
    setMetrics((previous) => (isSameMetrics(previous, next) ? previous : next));
  }, []);

  // Layout effect: the first measurement lands before paint, so no flash.
  useLayoutEffect(() => {
    measure();
  }, [measure, measureKey]);

  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    for (const node of [containerRef.current, measureRef.current, trailingRef.current]) {
      if (node) observer.observe(node);
    }
    return () => observer.disconnect();
  }, [measure]);

  useLayoutEffect(() => {
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    if (!fonts) return;
    let isActive = true;
    fonts.ready.then(() => {
      if (isActive) measure();
    });
    fonts.addEventListener('loadingdone', measure);
    return () => {
      isActive = false;
      fonts.removeEventListener('loadingdone', measure);
    };
  }, [measure]);

  const result = useMemo<VisibleTabsResult>(() => {
    if (!metrics || metrics.tabWidths.length !== tabCount) {
      return {
        visibleIndices: Array.from({ length: tabCount }, (_, index) => index),
        overflowIndices: [],
      };
    }
    return fitMeasuredTabs(metrics, activeIndex);
  }, [metrics, tabCount, activeIndex]);

  return {
    ...result,
    containerRef,
    measureRef,
    trailingRef,
    isMeasured: metrics != null && metrics.tabWidths.length === tabCount,
  };
}
